import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUpload, faImage } from '@fortawesome/free-solid-svg-icons';
import { t } from 'i18next';
import axios from 'axios';
import SettingsBlock from '../util/SettingsBlock';

interface TasksProps {
  urlImage: any;
  handleDeleteLogo: any;
  file: any;
  handleSubmit: any;
  setFile: any;
  companyName: any;
  handleTaskOrTasks: any;
  taskOrTasks: any;
  handleTimeFormatChange: any;
  is24HourFormat: any;
  access?: any;
}

const Main: React.FC<TasksProps> = ({
  urlImage, handleDeleteLogo, file, handleSubmit, setFile,
  companyName, handleTaskOrTasks, taskOrTasks,
  handleTimeFormatChange, is24HourFormat, access,
}) => {
  const [deploying, setDeploying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeploy = async () => {
    if (!window.confirm('Start server update? All users will see the maintenance page.')) return;
    setDeploying(true);
    try {
      await axios.post('admin/deploy');
      setTimeout(() => { window.location.href = '/maintenance.html'; }, 1500);
    } catch {
      setDeploying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Company Logo ── */}
      <SettingsBlock title={t('CompanyLogo') || 'Company Logo'}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>

          {/* Upload area */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !file && fileInputRef.current?.click()}
            style={{
              width: 160, height: 72, borderRadius: 8, flexShrink: 0,
              border: dragOver
                ? '2px solid #2563eb'
                : urlImage
                  ? '1px solid rgba(0,0,0,.08)'
                  : '2px dashed rgba(0,0,0,.12)',
              background: dragOver ? 'rgba(37,99,235,.04)' : 'rgba(0,0,0,.02)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: file ? 'default' : 'pointer',
              overflow: 'hidden', position: 'relative',
              transition: 'border-color .15s, background .15s',
            }}
          >
            {urlImage ? (
              <>
                <img src={urlImage} alt="logo"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); handleDeleteLogo(); }}
                  title="Remove logo"
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    width: 18, height: 18, borderRadius: '50%',
                    background: 'rgba(220,38,38,.85)', border: 'none',
                    color: '#fff', fontSize: 9, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                <FontAwesomeIcon icon={faImage} style={{ fontSize: 20, color: 'rgba(0,0,0,.18)' }} />
                <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                  {dragOver ? 'Drop here' : 'Drop or click'}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {file ? (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-color)', fontWeight: 600 }}>
                  {file.name}
                </div>
                <p style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={handleSubmit}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faUpload} style={{ fontSize: 10 }} />
                    {t('Upload logo')}
                  </button>
                  <button type="button" onClick={() => setFile(null)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,.09)', background: 'rgba(0,0,0,.04)', color: 'var(--text-color)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  style={{ width: 'fit-content', display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(0,0,0,.12)', background: 'rgba(0,0,0,.03)', color: 'var(--text-color)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faUpload} style={{ fontSize: 10 }} />
                  {t('Browse Logo')}
                </button>
                <p style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', lineHeight: 1.5 }}>
                  PNG, JPG, JPEG · max 1 MB<br />
                  Recommended: 200 × 40 px
                </p>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            title={t('Browse Logo') || 'Browse Logo'}
            accept=".png,.jpeg,.jpg"
            style={{ display: 'none' }}
            onChange={e => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </SettingsBlock>

      {/* ── Company Settings ── */}
      <SettingsBlock title={t('CompanyName') || 'Company'}>
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-color)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginRight: 6 }}>{t('CompanyName')}:</span>
            <span style={{ fontWeight: 600 }}>"{companyName}"</span>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={!taskOrTasks} onChange={handleTaskOrTasks}
              style={{ width: 15, height: 15, accentColor: '#2563eb', cursor: 'pointer' }} />
            <span style={{ fontSize: 13, color: 'var(--text-color)' }}>Single Task</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={!is24HourFormat} onChange={handleTimeFormatChange}
              style={{ width: 15, height: 15, accentColor: '#2563eb', cursor: 'pointer' }} />
            <span style={{ fontSize: 13, color: 'var(--text-color)' }}>Format time AM/PM</span>
          </label>
        </div>
      </SettingsBlock>

      {/* ── Server Update (admin only) ── */}
      {access?.description === -1 && (
        <SettingsBlock title="Server Update" defaultOpen={false}>
          <div style={{ padding: '12px 16px' }}>
            <p style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginBottom: 12, lineHeight: 1.6 }}>
              Enables maintenance mode and runs the deploy script. All users will see the maintenance page until the update is complete.
            </p>
            <button type="button" onClick={handleDeploy} disabled={deploying}
              style={{ background: deploying ? '#9ca3af' : '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: deploying ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              🚀 {deploying ? 'Starting...' : 'Update Server'}
            </button>
          </div>
        </SettingsBlock>
      )}

    </div>
  );
};

export default Main;
